import { Popover } from "@mui/material";
import {
  differenceInMinutes,
  differenceInSeconds,
  millisecondsInSecond,
} from "date-fns";
import {
  ReactNode,
  useCallback,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import toast from "react-hot-toast";
import { FeaturePermission } from "@/__generated__/featurePermissions";
import { useUser } from "@/auth/useUser";
import {
  ArrowTurnBackwardIcon,
  Clock02Icon,
  CoffeeIcon,
} from "@/components/common/icons";
import CashDrawerDialog from "@/components/serviceFlow/clockin/cashDrawerDialog";
import WorkShiftDialog, {
  WorkShiftDialogType,
} from "@/components/serviceFlow/clockin/workShiftDialog/workShiftDialog";
import ClockedInWorkShiftsTracker from "@/components/serviceFlow/clockin/workShiftsTracker/clockedInTracker";
import ClockedOutWorkShiftsTracker from "@/components/serviceFlow/clockin/workShiftsTracker/clockedOutTracker";
import OnBreakWorkShiftsTracker from "@/components/serviceFlow/clockin/workShiftsTracker/onBreakTracker";
import WorkShiftsTrackerPopup from "@/components/serviceFlow/clockin/workShiftsTracker/workShiftsTrackerPopup";
import { GREY } from "@/config/mui/colorPalette";
import {
  CurrentUserOpenShiftDocument,
  useCurrentUserOpenShiftQuery,
} from "@/graphql/currentUserOpenShift.graphql.types";
import { useEndBreakMutation } from "@/graphql/mutations/clockin/endBreak.graphql.types";
import { useEndWorkShiftMutation } from "@/graphql/mutations/clockin/endWorkShift.graphql.types";
import { useStartBreakMutation } from "@/graphql/mutations/clockin/startBreak.graphql.types";
import { useStartWorkShiftMutation } from "@/graphql/mutations/clockin/startWorkShift.graphql.types";
import { PayrollShiftsDocument } from "@/graphql/queries/clockin.graphql.types";
import { useMedspaId } from "@/hooks/common/useMedspaId";
import useMedspaTimezone from "@/hooks/common/useMedspaTimezone";
import { useUserMedspaId } from "@/hooks/common/useUserMedspa";
import useMoxieMediaQuery from "@/hooks/misc/useMoxieMediaQuery";
import { ShiftStatus } from "@/types";
import useErrorLogger from "@/utils/useErrorLogger";

export type WorkShiftsTrackerAction = {
  label: string;
  icon: ReactNode;
  onClick: () => void;
};

type WorkShiftsTrackerProps = {
  showAdditionalInfo?: boolean;
};

export default function WorkShiftsTracker({
  showAdditionalInfo = true,
}: WorkShiftsTrackerProps) {
  const { hasFeaturePermission } = useUser();
  const medspaId = useMedspaId();
  const { utcToMedspaZonedTime } = useMedspaTimezone(medspaId);
  const userMedspaId = useUserMedspaId();
  const isMobile = useMoxieMediaQuery("ipad-ver", "down");
  const logError = useErrorLogger();

  // we assume that this is less than 24 hours
  const [shiftDurationSeconds, setShiftDurationSeconds] = useState<
    number | undefined
  >(undefined);
  const [breaksCumulativeDurationMinutes, setBreaksCumulativeDurationMinutes] =
    useState(0);

  const [isWorkShiftDialogOpen, setIsWorkShiftDialogOpen] = useState(false);
  const [isCashDrawerDialogOpen, setIsCashDrawerDialogOpen] = useState(false);

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | undefined>(undefined);

  const canVerifyCashDrawer = hasFeaturePermission(
    FeaturePermission.VERIFY_CASH_ON_CLOCK_IN_OUT
  );

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setIsPopoverOpen(true);
  };

  const handlePopoverClose = () => {
    setAnchorEl(undefined);
    setIsPopoverOpen(false);
  };

  const [startWorkShift] = useStartWorkShiftMutation({
    onError: (error) => {
      logError(error, "Failed to start work shift");
      toast.error("Failed to start shift. Please try again.");
    },
  });

  const [endWorkShift] = useEndWorkShiftMutation({
    onError: (error) => {
      logError(error, "Failed to end work shift");
      toast.error("Failed to end shift. Please try again.");
    },
  });

  const [startBreak] = useStartBreakMutation({
    onError: (error) => {
      logError(error, "Failed to start break");
      toast.error("Failed to start break. Please try again.");
    },
  });

  const [endBreak] = useEndBreakMutation({
    onError: (error) => {
      logError(error, "Failed to end break");
      toast.error("Failed to end break. Please try again.");
    },
  });

  const { data } = useCurrentUserOpenShiftQuery({
    variables: {
      userMedspaId,
    },
  });

  const loading = !data;

  const payrollShift = data?.userMedspaByPk?.payrollShifts?.[0];

  const shiftStartDate = utcToMedspaZonedTime(payrollShift?.startTime);

  const updateShiftDuration = useCallback(() => {
    if (!data) return;

    const currentTime = utcToMedspaZonedTime(new Date().toISOString());
    const differenceSeconds = data
      ? differenceInSeconds(currentTime, shiftStartDate)
      : 0;

    setShiftDurationSeconds(differenceSeconds);
  }, [data, shiftStartDate, utcToMedspaZonedTime]);

  const updateBreaksCumulativeDuration = useCallback(() => {
    if (!data) return;

    const breaksTotalDuration = payrollShift?.payrollBreaks.reduce(
      (total, currentBreak) => {
        if (currentBreak.endTime) {
          total += differenceInMinutes(
            utcToMedspaZonedTime(currentBreak.endTime),
            utcToMedspaZonedTime(currentBreak.startTime)
          );
        } else {
          total += differenceInMinutes(
            utcToMedspaZonedTime(new Date().toISOString()),
            utcToMedspaZonedTime(currentBreak.startTime)
          );
        }

        return total;
      },
      0
    );

    setBreaksCumulativeDurationMinutes(breaksTotalDuration);
  }, [data, payrollShift, utcToMedspaZonedTime]);

  useLayoutEffect(() => {
    const intervals = [];

    updateShiftDuration();
    const updateShiftDurationInterval = setInterval(
      () => updateShiftDuration(),
      millisecondsInSecond
    );
    intervals.push(updateShiftDurationInterval);

    updateBreaksCumulativeDuration();
    const updateBreaksCumulativeDurationInterval = setInterval(
      () => updateBreaksCumulativeDuration(),
      millisecondsInSecond
    );
    intervals.push(updateBreaksCumulativeDurationInterval);

    return () => intervals.forEach(clearInterval);
  }, [data, updateShiftDuration, updateBreaksCumulativeDuration]);

  const shiftStatus = useMemo(() => {
    if (!payrollShift?.startTime) {
      return ShiftStatus.CLOCKED_OUT;
    }

    // breaks are ordered by endTime ASC with nulls last
    const breaks = payrollShift?.payrollBreaks || [];
    const lastBreak = breaks[breaks.length - 1];

    if (!lastBreak || lastBreak.endTime) {
      return ShiftStatus.CLOCKED_IN;
    }

    return ShiftStatus.ON_BREAK;
  }, [payrollShift]);

  const handleStartShift = async () => {
    await startWorkShift({
      variables: {
        medspaId,
      },
      refetchQueries: [CurrentUserOpenShiftDocument],
    });
    setIsWorkShiftDialogOpen(false);
    if (canVerifyCashDrawer) setIsCashDrawerDialogOpen(true);
  };

  const handleEndShift = async () => {
    await endWorkShift({
      variables: {
        medspaId,
      },
      refetchQueries: [CurrentUserOpenShiftDocument, PayrollShiftsDocument],
    });
    setIsWorkShiftDialogOpen(false);
    if (canVerifyCashDrawer) setIsCashDrawerDialogOpen(true);
  };

  const handleStartBreak = async () => {
    await startBreak({
      variables: {
        medspaId,
      },
      refetchQueries: [CurrentUserOpenShiftDocument],
    });
    handlePopoverClose();
  };

  const handleEndBreak = async () => {
    await endBreak({
      variables: {
        medspaId,
      },
      refetchQueries: [CurrentUserOpenShiftDocument],
    });
    handlePopoverClose();
  };

  const actions: WorkShiftsTrackerAction[] = [
    shiftStatus === ShiftStatus.CLOCKED_IN && {
      label: "Go on a break",
      icon: <CoffeeIcon size={20} color={GREY[70]} />,
      onClick: handleStartBreak,
    },
    shiftStatus === ShiftStatus.ON_BREAK && {
      label: "Return from a break",
      icon: <ArrowTurnBackwardIcon size={20} color={GREY[70]} />,
      onClick: handleEndBreak,
    },
    {
      label: "Clock out",
      icon: <Clock02Icon size={20} color={GREY[70]} strokeWidth={1.25} />,
      onClick: () => setIsWorkShiftDialogOpen(true),
    },
  ].filter(Boolean);

  return (
    <>
      {shiftStatus === ShiftStatus.CLOCKED_IN && (
        <ClockedInWorkShiftsTracker
          shiftDurationSeconds={shiftDurationSeconds}
          showAdditionalInfo={showAdditionalInfo}
          handleClick={handleClick}
        />
      )}
      {shiftStatus === ShiftStatus.ON_BREAK && (
        <OnBreakWorkShiftsTracker
          shiftDurationSeconds={shiftDurationSeconds}
          showAdditionalInfo={showAdditionalInfo}
          handleClick={handleClick}
        />
      )}
      {shiftStatus === ShiftStatus.CLOCKED_OUT && (
        <ClockedOutWorkShiftsTracker
          showAdditionalInfo={showAdditionalInfo}
          handleClick={() => setIsWorkShiftDialogOpen(true)}
          disabled={loading}
        />
      )}
      <Popover
        open={isPopoverOpen}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: isMobile ? -20 : -8,
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
            },
          },
        }}
      >
        {shiftStatus !== ShiftStatus.CLOCKED_OUT && (
          <WorkShiftsTrackerPopup
            actions={actions}
            shiftStartDate={shiftStartDate}
            shiftDurationSeconds={shiftDurationSeconds}
            breaksCumulativeDurationMinutes={breaksCumulativeDurationMinutes}
            shiftStatus={shiftStatus}
          />
        )}
      </Popover>
      <WorkShiftDialog
        {...(payrollShift?.startTime
          ? {
              type: WorkShiftDialogType.END,
              isOpen: isWorkShiftDialogOpen,
              shiftStartDate,
              breaksCumulativeDurationMinutes,
              handleEndShift: () => {
                handleEndShift(); // move cash drawer here
                if (canVerifyCashDrawer) setIsCashDrawerDialogOpen(true);
              },
              handleCancel: () => setIsWorkShiftDialogOpen(false),
            }
          : {
              type: WorkShiftDialogType.START,
              isOpen: isWorkShiftDialogOpen,
              handleStartShift,
              handleCancel: () => setIsWorkShiftDialogOpen(false),
            })}
      />
      <CashDrawerDialog
        isOpen={isCashDrawerDialogOpen}
        onSuccess={() => setIsCashDrawerDialogOpen(false)}
        onSkip={() => setIsCashDrawerDialogOpen(false)}
      />
    </>
  );
}
